import React, { Component } from 'react';
import { CLOUD_PROVIDERS, PLAN_TYPES, PLAN_SIZES } from 'app/util/constants';
import { deviceTypeByKflow, zeroToText } from 'app/util/utils';
import { observer } from 'mobx-react';
import { Checkbox, CheckboxGroup, Field, FormDialog, InputGroup, NumericInput, Select, TextArea } from 'core/form';
import { Box, Callout, Flex } from 'core/components';
import Text from 'core/components/Text';

import LargeLabeledValue from 'app/components/LargeLabeledValue';

function getPlanTypeOptions() {
  return Object.values(PLAN_TYPES).map((planType) => ({ value: planType.code, label: planType.name }));
}

function NmsCallout({ currentDeviceCount, maxDevices }) {
  const ratio = currentDeviceCount / maxDevices;
  let intent = 'primary';
  let icon = 'info-sign';
  const buffer = maxDevices >= 100 ? 100 : 0;
  const overage = currentDeviceCount > maxDevices ? currentDeviceCount - maxDevices : 0;
  let message =
    'When "entitled devices" is less than 100, the customer will not be afforded the 100-device licensing buffer.';
  if (ratio >= 1 && buffer && overage < 100) {
    intent = 'warning';
    icon = 'warning-sign';
    message = `This account has reached their device limit and has used ${overage} of  buffer.`;
  } else if (ratio >= 1 && (!buffer || overage >= 100)) {
    intent = 'danger';
    icon = 'error';
    // TODO: Add 'They can no longer add additional devices.' when we have a way to prevent adding devices.
    message = 'This account has reached their device limit and consumed their overage buffer.';
  }

  return (
    <Callout alignItems="center" icon={icon} intent={intent}>
      <Text as="div" ml={3} style={{ marginTop: '-2.5px' }}>
        {message}
      </Text>
    </Callout>
  );
}

const options = {
  name: 'Company Plan'
};
@observer
export default class CompanyPlansFormDialog extends Component {
  get fields() {
    return {
      name: {
        label: 'Name',
        rules: 'required'
      },
      description: {
        label: 'Description (visible to customers)',
        rules: 'required'
      },
      max_fps: {
        label: 'Max FPS',
        rules: 'required|integer|min:1',
        defaultValue: 2000
      },
      bgp: {
        label: 'Enable BGP',
        defaultValue: true
      },
      fast_retention: {
        label: (
          <span key="fast_retention">
            Trending Data Retention{' '}
            <span key="fast_retention_sub" className="pt-text-muted">
              (days)
            </span>
          </span>
        ),
        rules: 'required|min:1',
        defaultValue: 90
      },
      full_retention: {
        label: (
          <span key="full_retention">
            Forensic Data Retention{' '}
            <span key="full_retention_sub" className="pt-text-muted">
              (days)
            </span>
          </span>
        ),
        rules: 'required|min:1',
        defaultValue: 90
      },
      max_devices: {
        label: 'Maximum # of Data Sources',
        rules: 'required|min:1',
        defaultValue: 25
      },
      deviceTypes: {
        label: 'Allowed Device Types',
        // See utils.kflowTypeLabelMap for kflowType => label translation.
        defaultValue: deviceTypeByKflow('router', 'host-nprobe-dns-www').map((dt) => dt.id),
        options: deviceTypeByKflow('router', 'host-nprobe-basic', 'host-nprobe-dns-www', 'host').map((dt) => ({
          value: dt.id,
          label: dt.label
        })),
        rules: 'required',
        transform: {
          in: (value) => value.map((type) => type.id || type)
        }
      },
      'metadata.type': {
        label: 'Plan Type',
        defaultValue: 'legacy',
        options: getPlanTypeOptions()
      },
      'metadata.cloudProvider': {
        label: 'Cloud Provider',
        options: [
          { value: CLOUD_PROVIDERS.GCE.id, label: CLOUD_PROVIDERS.GCE.name },
          { value: CLOUD_PROVIDERS.AWS.id, label: CLOUD_PROVIDERS.AWS.name },
          { value: CLOUD_PROVIDERS.AZURE.id, label: CLOUD_PROVIDERS.AZURE.name },
          { value: 'universal', label: 'Universal' }
        ]
      },
      'metadata.pakFps': {
        label: 'Customer Facing Pak FPS'
      },
      'metadata.isPak': {
        label: 'Share Max FPS Quota between Devices',
        defaultValue: false
      },
      'metadata.resolution': {
        label: 'Resolution',
        defaultValue: 'std',
        options: [
          { value: 'std', label: 'Standard' },
          { value: 'enh', label: 'Enhanced' }
        ]
      },
      'metadata.size': {
        label: 'Plan Size'
      },
      'metadata.paidCredits': {
        label: 'Paid Credits',
        defaultValue: 0
      },
      'metadata.freeCredits': {
        label: 'Free Credits',
        defaultValue: 0
      },
      'metadata.maxSnapshots': {
        label: 'Max Snapshots (-1 for Unlimited)',
        rules: 'integer|min:-1',
        defaultValue: 0
      },
      'metadata.maxRate': {
        label: 'Max Rate (rps)',
        defaultValue: 0
      }
    };
  }

  isCloudPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && (typeField.value === PLAN_TYPES.cloud.code || typeField.value === PLAN_TYPES.universalpak.code);
  };

  isMetricsPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && typeField.value === PLAN_TYPES.metrics.code;
  };

  isPak = (form) => {
    const typeField = form.getField('metadata.type');
    return (
      typeField &&
      (typeField.value === PLAN_TYPES.cloud.code ||
        typeField.value === PLAN_TYPES.flowpak.code ||
        typeField.value === PLAN_TYPES.universalpak.code)
    );
  };

  isUniversalPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && typeField.value === PLAN_TYPES.universalpak.code;
  };

  isPlatformPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && (typeField.value === PLAN_TYPES.edge.code || typeField.value === PLAN_TYPES.core.code);
  };

  isSyntheticsPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && typeField.value === PLAN_TYPES.ksynth.code;
  };

  isTrafficCostsPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && typeField.value === PLAN_TYPES.trafficcosts.code;
  };

  isDnsAnalyticsPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && typeField.value === PLAN_TYPES.dns_analytics.code;
  };

  isNmsPlan = (form) => {
    const typeField = form.getField('metadata.type');
    return typeField && typeField.value === PLAN_TYPES.nmspak.code;
  };

  getCurrentMaxDevices = (form) => {
    const typeField = form.getField('max_devices');
    return typeField && typeField.value;
  };

  updateCloudProviderRules = (form) => {
    const providerField = form.getField('metadata.cloudProvider');
    if (providerField) {
      providerField.setRules(this.isCloudPlan(form) ? 'required' : '');
    }
  };

  updatePakRules = (form) => {
    const pakFpsField = form.getField('metadata.pakFps');
    pakFpsField.setRules(this.isPak(form) ? 'required|numeric|min:1' : '');
    form.getField('max_fps').setLabel(this.isPak(form) ? 'Max FPS (incl. buffer)' : 'Max FPS');
  };

  updateMetricsRules = (form) => {
    // Labels get applied on all updates.
    const { full_retention, max_devices, max_fps } = this.fields;
    if (this.isMetricsPlan(form)) {
      form.getField('max_fps').setLabel('Max Metrics/second');
      form.getField('max_devices').setLabel('Max Devices');
      form.getField('full_retention').setLabel('Data Retention (days)');
    } else if (this.isNmsPlan(form)) {
      form.getField('full_retention').setLabel('Data Retention (days)');
      form.getField('max_devices').setLabel('Entitled Devices');
    } else if (!this.isPak(form)) {
      form.getField('max_fps').setLabel(max_fps.label);
    } else {
      form.getField('max_devices').setLabel(max_devices.label);
      form.getField('full_retention').setLabel(full_retention.label);
    }
  };

  getPlanSizeOptions = (form) => {
    const typeField = form.getField('metadata.type');
    const type = typeField && typeField.value;
    const allOptions = Object.values(PLAN_SIZES).map((psObj) => ({ value: psObj.code, label: psObj.name }));

    // If plan type is edge, pull out 1GB option, otherwise return all.
    if (type === PLAN_TYPES.edge.code) {
      return allOptions.filter((opt) => opt.value !== PLAN_SIZES['1GB'].code);
    }
    return allOptions;
  };

  updatePlatformPlanRules = (form) => {
    const sizeField = form.getField('metadata.size');
    const resolutionField = form.getField('metadata.resolution');
    const isPlatformPlan = this.isPlatformPlan(form);
    resolutionField.setRules(isPlatformPlan ? 'required' : '');
    sizeField.setRules(isPlatformPlan ? 'required' : '');
  };

  updateRules = (form) => {
    this.updateCloudProviderRules(form);
    this.updatePakRules(form);
    this.updateMetricsRules(form);
    this.updatePlatformPlanRules(form);
  };

  handleSave = (form, values) => {
    const { model, company } = this.props;

    return model.save(values).then(() => company.fetch());
  };

  handleExpire = () => {
    const { model, company } = this.props;

    return model.destroy({ remove: false }).then(() => company.fetch());
  };

  handlePlanTypeChange = (planTypeFieldState) => {
    const { form } = planTypeFieldState;
    const routerDeviceTypeId = deviceTypeByKflow('router')[0].id;
    const kProbeDeviceTypeId = deviceTypeByKflow('host-nprobe-dns-www')[0].id;

    // if cloud set device type to kprobe
    const type = planTypeFieldState.value;

    if (this.isUniversalPlan(form)) {
      form.getField('deviceTypes').setValue([routerDeviceTypeId, kProbeDeviceTypeId]);
      form.getField('metadata.cloudProvider').setValue('universal');
    } else if (this.isCloudPlan(form)) {
      form.getField('deviceTypes').setValue([kProbeDeviceTypeId]);
      form.getField('metadata.cloudProvider').setValue('universal');
    } else {
      form.getField('deviceTypes').setValue([routerDeviceTypeId, kProbeDeviceTypeId]);
      form.getField('metadata.cloudProvider').setValue('');
    }

    if (this.isPak(form)) {
      form.getField('metadata.pakFps').setValue(2000);
      form.getField('max_fps').setValue(4000);
      form.getField('metadata.isPak').setValue(true);
      form.getField('max_devices').setValue(this.isCloudPlan(form) ? 10000 : 100);
    } else {
      form.getField('metadata.pakFps').setValue('');
      form.getField('max_fps').setValue(this.isMetricsPlan(form) ? 15000 : 2000);
      form.getField('metadata.isPak').setValue(false);
      form.getField('max_devices').setValue(this.isMetricsPlan(form) ? 500 : 25);
    }

    if (this.isPlatformPlan(form)) {
      form.getField('metadata.resolution').setValue('std');
      if (type === PLAN_TYPES.edge.code) {
        // set default size for edge/core platform device plans
        form.getField('metadata.size').setValue(PLAN_SIZES['10GB'].code);
      }
      if (type === PLAN_TYPES.core.code) {
        form.getField('metadata.size').setValue(PLAN_SIZES['1GB'].code);
      }
    } else {
      form.getField('metadata.size').setValue('');
      form.getField('metadata.resolution').setValue('');
    }

    if (this.isPlatformPlan(form) || this.isCloudPlan(form) || this.isPak(form)) {
      form.getField('fast_retention').setValue(120);
      form.getField('full_retention').setValue(45);
    } else {
      form.getField('fast_retention').setValue(90);
      form.getField('full_retention').setValue(90);
    }

    if (this.isMetricsPlan(form)) {
      // Value only gets overwritten on new plan.
      form.getField('full_retention').setValue(400);
    }

    if (this.isSyntheticsPlan(form)) {
      form.getField('max_fps').setValue(10000);
      form.getField('fast_retention').setValue(365);
      form.getField('full_retention').setValue(365);
      form.getField('max_devices').setValue(1);
      form.getField('deviceTypes').setValue([routerDeviceTypeId]);
      form.getField('metadata.freeCredits').setValue(2.5 * 10 ** 6);
      form.getField('metadata.paidCredits').setValue(0);
      form.getField('name').setValue('Synthetics');
      form.getField('description').setValue('Synthetics Plan');
    } else {
      form.getField('metadata.freeCredits').setValue('');
      form.getField('metadata.paidCredits').setValue('');
      form.getField('name').setValue('');
      form.getField('description').setValue('');
    }

    if (this.isTrafficCostsPlan(form)) {
      form.getField('max_fps').setValue(10);
      form.getField('fast_retention').setValue(30);
      form.getField('full_retention').setValue(30);
      form.getField('max_devices').setValue(1);
      form.getField('deviceTypes').setValue([routerDeviceTypeId]);
      form.getField('metadata.freeCredits').setValue(0);
      form.getField('metadata.paidCredits').setValue(0);
      form.getField('metadata.maxSnapshots').setValue(1200);
      form.getField('name').setValue('Traffic Costs Plan');
      form.getField('description').setValue('Traffic Costs Plan');
    } else {
      form.getField('metadata.maxSnapshots').setValue('');
    }

    if (this.isNmsPlan(form)) {
      form.getField('max_fps').setValue(1);
      form.getField('fast_retention').setValue(90);
      form.getField('full_retention').setValue(90);
      form.getField('max_devices').setValue(25);
      form.getField('name').setValue('NPak');
      form.getField('description').setValue('Default Npak Plan');
      form.getField('metadata.freeCredits').setValue(0);
      form.getField('metadata.paidCredits').setValue(0);
      form.getField('metadata.maxSnapshots').setValue('');
    }

    if (this.isDnsAnalyticsPlan(form)) {
      form.getField('name').setValue('DNS Analytics Plan');
      form.getField('description').setValue('DNS Analytics Plan');
    } else {
      form.getField('metadata.maxRate').setValue('');
    }
    this.updateRules(form);
  };

  renderSyntheticsFields(form) {
    return (
      <>
        <Field name="metadata.freeCredits">
          <NumericInput />
        </Field>
        <Field name="metadata.paidCredits">
          <NumericInput />
        </Field>
        <LargeLabeledValue
          label="Total Synthetics Credits"
          value={zeroToText(
            parseInt(form.getValue('metadata.freeCredits') || 0) + parseInt(form.getValue('metadata.paidCredits') || 0),
            { fix: 0 }
          )}
        />
      </>
    );
  }

  render() {
    const { model, onClose, company } = this.props;
    return (
      <FormDialog
        model={model}
        fields={this.fields}
        options={options}
        entityName="Plan"
        isOpen
        onClose={onClose}
        width={600}
        formActionsProps={{
          onSubmit: this.handleSave,
          onCancel: onClose,
          onRemove: this.handleExpire,
          showRemove: true
        }}
        formComponentProps={{
          onLoad: (form) => this.updateRules(form)
        }}
      >
        {({ form }) => {
          const isSynthPlan = this.isSyntheticsPlan(form);
          const isCloudPlan = this.isCloudPlan(form);
          const isPlatformPlan = this.isPlatformPlan(form);
          const isMetricsPlan = this.isMetricsPlan(form);
          const isTrafficCostsPlan = this.isTrafficCostsPlan(form);
          const isDnsAnalyticsPlan = this.isDnsAnalyticsPlan(form);
          const isNmsPlan = this.isNmsPlan(form);

          let nmsMaxDevices = 25;
          let nmsCurrentDevices = 0;

          const { nmsPlan = null } = company;

          if (isNmsPlan && nmsPlan) {
            nmsCurrentDevices = nmsPlan?.numDevices ?? 0;
            nmsMaxDevices = this.getCurrentMaxDevices(form) ?? 25;
          }

          return (
            <Flex flexDirection="column" flexWrap gutter={2}>
              <Flex gutter={2} justifyContent="space-between" flexAuto>
                <Box flexAuto px={2}>
                  <Field name="metadata.type" onChange={this.handlePlanTypeChange}>
                    <Select />
                  </Field>
                  {isSynthPlan && this.renderSyntheticsFields(form)}
                  {isCloudPlan && (
                    <Field name="metadata.cloudProvider">
                      <Select />
                    </Field>
                  )}
                  {isPlatformPlan && (
                    <>
                      <Field name="metadata.resolution">
                        <Select />
                      </Field>
                      <Field name="metadata.size" options={this.getPlanSizeOptions(form)}>
                        <Select />
                      </Field>
                    </>
                  )}
                  <Field name="name">
                    <InputGroup autoFocus />
                  </Field>
                  {!isTrafficCostsPlan && (
                    <Field name="description">
                      <TextArea rows={4} />
                    </Field>
                  )}
                  {!isSynthPlan && !isMetricsPlan && !isTrafficCostsPlan && !isDnsAnalyticsPlan && !isNmsPlan && (
                    <Field name="bgp" showLabel={false}>
                      <Checkbox />
                    </Field>
                  )}
                  {!isCloudPlan &&
                    !isSynthPlan &&
                    !isMetricsPlan &&
                    !isTrafficCostsPlan &&
                    !isDnsAnalyticsPlan &&
                    !isNmsPlan && (
                      <Field name="deviceTypes">
                        <CheckboxGroup />
                      </Field>
                    )}
                </Box>
                <Box px={2} width={250}>
                  {this.isPak(form) && (
                    <>
                      <Field name="metadata.pakFps">
                        <NumericInput />
                      </Field>
                      <Field name="metadata.isPak" showLabel={false}>
                        <Checkbox />
                      </Field>
                    </>
                  )}

                  {isTrafficCostsPlan && (
                    <Field name="metadata.maxSnapshots">
                      <NumericInput />
                    </Field>
                  )}

                  {!isSynthPlan && !isTrafficCostsPlan && !isDnsAnalyticsPlan && !isNmsPlan && (
                    <Field name="max_fps">
                      <NumericInput />
                    </Field>
                  )}

                  {!isTrafficCostsPlan && !isDnsAnalyticsPlan && (
                    <Field name="full_retention">
                      <NumericInput />
                    </Field>
                  )}
                  {!isMetricsPlan && !isTrafficCostsPlan && !isDnsAnalyticsPlan && !isNmsPlan && (
                    <Field name="fast_retention">
                      <NumericInput />
                    </Field>
                  )}
                  {!isTrafficCostsPlan && !isDnsAnalyticsPlan && (
                    <Field name="max_devices">
                      <NumericInput />
                    </Field>
                  )}
                  {isDnsAnalyticsPlan && (
                    <Field name="metadata.maxRate">
                      <NumericInput />
                    </Field>
                  )}
                </Box>
              </Flex>
              {isNmsPlan && <NmsCallout currentDeviceCount={nmsCurrentDevices} maxDevices={nmsMaxDevices} />}
            </Flex>
          );
        }}
      </FormDialog>
    );
  }
}
